/**
 * useSamplePositions — Comprehensive mock data for Position Tracker development.
 *
 * Generates 5 NBA games with realistic orders, positions, spread markets,
 * and calculated P&L. Activated via the toggle in CommandStrip (no query param).
 *
 * Uses stable useMemo so the mock data is generated once per mount.
 */

import { useMemo } from 'react';
import type { Position } from '@/types';
import type { KalshiOrder } from '@/lib/kalshiApi';
import type { ActivityEvent } from '@/contexts/ActivityContext';
import type { MarketBook } from '@/lib/sportsStream/types';
import type { ConsolidatedGameBooks } from '@/lib/nbaConsolidated/types';
import type { GameData } from '@/lib/sportsStream/types';
import { generateMockTraderState } from '@/mock/trader';

export interface SamplePositionsResult {
  positions: Position[];
  orders: KalshiOrder[];
  totalPnl: number;
  titleByTicker: Record<string, string>;
  dateByTicker: Record<string, string>;
  outcomeByTicker: Record<string, { yes?: string; no?: string }>;
  activityEvents: ActivityEvent[];
  marketBooks: Map<string, MarketBook>;
  consolidatedGames: ConsolidatedGameBooks[];
  sportsGames: Map<string, GameData[]>;
}

export function useSamplePositions(
  sharedMock?: ReturnType<typeof generateMockTraderState>
): SamplePositionsResult {
  return useMemo(() => {
    const mock = sharedMock ?? generateMockTraderState();

    const marketBooks = new Map<string, MarketBook>();
    for (const game of mock.consolidatedGames) {
      if (game.kalshi) {
        const { away, home } = game.kalshi.markets;
        if (away.marketTicker) {
          marketBooks.set(away.marketTicker, {
            marketTicker: away.marketTicker,
            yes: away.yes,
            no: away.no,
            tsMs: game.kalshi.tsMs,
          });
        }
        if (home.marketTicker) {
          marketBooks.set(home.marketTicker, {
            marketTicker: home.marketTicker,
            yes: home.yes,
            no: home.no,
            tsMs: game.kalshi.tsMs,
          });
        }
      }
    }

    const now = Date.now();
    const activityEvents: ActivityEvent[] = mock.positions.slice(0, 4).map((p, i) => ({
      id: `mock-fill-${i}`,
      type: 'order_filled' as const,
      message: `Filled ${p.quantity.toLocaleString()} ${p.side.toUpperCase()} @ ${(p.averagePrice as number).toFixed(0)}¢ — ${p.ticker}`,
      amount: -((p.averagePrice / 100) * p.quantity),
      ticker: p.ticker,
      timestamp: now - (i + 1) * 30_000,
    }));

    return {
      positions: mock.positions,
      orders: mock.orders,
      totalPnl: mock.totalPnl,
      titleByTicker: mock.positionTitleByTicker,
      dateByTicker: mock.positionGameDateByTicker,
      outcomeByTicker: mock.positionOutcomeByTicker,
      activityEvents,
      marketBooks,
      consolidatedGames: mock.consolidatedGames,
      sportsGames: mock.sportsGames,
    };
  }, [sharedMock]);
}
